/**
 * Tenant provisioning hardening for --setup (Azure CLI backed).
 *
 * Fresh tenants that have never used Power Platform fail setup two ways:
 *   AADSTS650052 — a resource service principal (Environment Service,
 *                  PowerApps Service, …) doesn't exist in the tenant yet.
 *   AADSTS65006  — after creating the SP, it publishes its scopes under
 *                  TENANT-LOCAL ids that don't match the canonical ids a
 *                  manifest hardcodes.
 *
 * The fix implemented here: treat hardcoded scope ids only as defaults.
 * Ensure every resource SP exists, read its live oauth2PermissionScopes,
 * resolve each desired scope VALUE ("User", "Flows.Manage.All", …) to the
 * id published ON THIS TENANT'S SP, and PATCH the app registration with
 * the resolved set. Scope values are stable across tenants; scope ids are
 * not for freshly-created first-party SPs.
 *
 * All Graph traffic goes through the Azure CLI (`az ad …` / `az rest`) so
 * provisioning runs with the signed-in admin's delegated rights — an
 * app-only token with just Directory.ReadWrite.All 403s on SP creation.
 */

import { writeFileSync, mkdtempSync, rmSync } from "fs";
import { join } from "path";
import { tmpdir } from "os";
import { runAzCommand } from "./app-registration.js";
import {
  getDesiredResourceScopesForFeatures,
  type DesiredResourceScopes,
  type RequiredResourceAccess,
} from "./published-app.js";
import type { FeatureId } from "../config/features.js";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface PublishedScope {
  id: string;
  value: string;
  isEnabled: boolean;
}

export interface ServicePrincipalInfo {
  /** Directory object id of the SP */
  id: string;
  appId: string;
  displayName?: string;
  oauth2PermissionScopes: PublishedScope[];
}

export interface ScopeResolution {
  resourceAppId: string;
  api: string;
  resolved: Array<{ value: string; id: string }>;
  /** Enabled delegated-scope ids currently published by the resource SP. */
  publishedScopeIds: string[];
  /** Desired values that could not be matched on the live SP */
  unresolved: string[];
  /** True when the single-published-scope fallback was used for some value */
  usedSingleScopeFallback: boolean;
  /** True when the SP had to be created in this tenant */
  createdServicePrincipal: boolean;
}

export interface ProvisioningReport {
  requiredResourceAccess: RequiredResourceAccess[];
  resolutions: ScopeResolution[];
  /** Non-fatal issues worth surfacing in the wizard */
  warnings: string[];
}

export type DirectoryResourceAccess = {
  id: string;
  type: "Scope" | "Role";
};

/**
 * The directory can contain both delegated scopes and application roles,
 * even though this MCP only adds delegated scopes itself.
 */
export interface DirectoryRequiredResourceAccess {
  resourceAppId: string;
  resourceAccess: DirectoryResourceAccess[];
}

export interface BuildResolvedResourceAccessOptions {
  ensureServicePrincipal?: typeof ensureServicePrincipal;
  getServicePrincipal?: typeof getServicePrincipal;
  sleep?: (ms: number) => Promise<void>;
  /** Override only for deterministic/fast tests. */
  scopeResolutionRetryDelaysMs?: readonly number[];
}

const POWER_PLATFORM_API_APP_ID = "8578e004-a5c6-46e7-913e-12f58912df43";
const REQUIRED_POWER_PLATFORM_SCOPE_VALUES = [
  "ResourceQuery.Resources.Read",
  "CopilotStudio.Copilots.Invoke",
  "CopilotStudio.MakerOperations.Read",
  "CopilotStudio.MakerOperations.ReadWrite",
  "CopilotStudio.AdminActions.Invoke",
  "PowerPages.Websites.Read",
  "PowerPages.Websites.Write",
] as const;
const REQUIRED_POWER_PLATFORM_SCOPE_VALUE_SET = new Set<string>(
  REQUIRED_POWER_PLATFORM_SCOPE_VALUES,
);
const DEFAULT_SCOPE_RESOLUTION_RETRY_DELAYS_MS = [1_000, 2_000, 4_000, 8_000] as const;

/**
 * Raised before an app-registration PATCH when required Power Platform API
 * scopes are not yet visible on the tenant's service principal.
 */
export class RequiredScopeResolutionError extends Error {
  readonly code = "REQUIRED_SCOPE_RESOLUTION_FAILED";
  readonly resourceAppId: string;
  readonly unresolved: string[];

  constructor(resourceAppId: string, unresolved: string[], publishedValues: string[]) {
    super(
      `Power Platform API does not publish the required delegated permission(s) ` +
        `${unresolved.map((value) => `"${value}"`).join(", ")} in this tenant yet. ` +
        `No app permissions were changed. Wait for Microsoft Entra directory propagation, ` +
        `ensure the service principal exists with ` +
        `\`az ad sp create --id ${POWER_PLATFORM_API_APP_ID}\`, then re-run ` +
        `\`powerautomate-mcp --setup\`. Published values: ` +
        `${publishedValues.join(", ") || "(none)"}`,
    );
    this.name = "RequiredScopeResolutionError";
    this.resourceAppId = resourceAppId;
    this.unresolved = [...unresolved];
  }
}

function assertUuid(value: string, label: string): void {
  if (!UUID_RE.test(value)) {
    throw new Error(`Invalid ${label}: ${value.substring(0, 40)}`);
  }
}

/**
 * Fetch a service principal by appId. Returns null when the tenant has no SP
 * for that appId (the AADSTS650052 precondition).
 */
export async function getServicePrincipal(appId: string): Promise<ServicePrincipalInfo | null> {
  assertUuid(appId, "resource appId");
  try {
    const output = await runAzCommand(["ad", "sp", "show", "--id", appId, "--output", "json"]);
    const sp = JSON.parse(output) as {
      id?: string;
      appId?: string;
      displayName?: string;
      oauth2PermissionScopes?: Array<{ id?: string; value?: string; isEnabled?: boolean }>;
    };
    if (!sp.id || !sp.appId) return null;
    return {
      id: sp.id,
      appId: sp.appId,
      displayName: sp.displayName,
      oauth2PermissionScopes: (sp.oauth2PermissionScopes ?? [])
        .filter((s): s is { id: string; value: string; isEnabled?: boolean } => !!s.id && !!s.value)
        .map((s) => ({ id: s.id, value: s.value, isEnabled: s.isEnabled !== false })),
    };
  } catch {
    return null;
  }
}

/**
 * Ensure a service principal exists for the given appId, creating it when
 * missing (fixes AADSTS650052 for first-party resources in fresh tenants).
 */
export async function ensureServicePrincipal(
  appId: string,
): Promise<{ sp: ServicePrincipalInfo; created: boolean }> {
  const existing = await getServicePrincipal(appId);
  if (existing) return { sp: existing, created: false };

  assertUuid(appId, "resource appId");
  await runAzCommand(["ad", "sp", "create", "--id", appId, "--output", "json"]);

  const created = await getServicePrincipal(appId);
  if (!created) {
    throw new Error(
      `Service principal for ${appId} was created but cannot be read back — ` +
        `directory propagation may be in progress. Re-run --setup in a minute.`,
    );
  }
  return { sp: created, created: true };
}

/**
 * Pure scope-id resolution: match desired scope VALUES against the scopes a
 * live SP publishes. Falls back to the SP's single published scope when the
 * desired value isn't found and the SP publishes exactly one enabled scope
 * (observed drift: a freshly-created Environment Service SP publishes "User"
 * where the canonical SP publishes "user_impersonation").
 *
 * Exported for unit testing — no I/O.
 */
export function resolveScopeIds(
  published: PublishedScope[],
  desiredValues: string[],
  alternateValues: string[] = [],
): {
  resolved: Array<{ value: string; id: string }>;
  unresolved: string[];
  usedSingleScopeFallback: boolean;
} {
  const enabled = published.filter((s) => s.isEnabled);
  const byValue = new Map(enabled.map((s) => [s.value.toLowerCase(), s]));
  const resolved: Array<{ value: string; id: string }> = [];
  const unresolved: string[] = [];
  let usedSingleScopeFallback = false;

  for (const value of desiredValues) {
    const match = byValue.get(value.toLowerCase());
    if (match) {
      resolved.push({ value: match.value, id: match.id });
      continue;
    }
    // Known alternate published values (observed per-tenant drift)
    const alternate = alternateValues
      .map((alt) => byValue.get(alt.toLowerCase()))
      .find((s) => s !== undefined);
    if (alternate) {
      resolved.push({ value: alternate.value, id: alternate.id });
      usedSingleScopeFallback = true;
      continue;
    }
    if (enabled.length === 1 && desiredValues.length === 1) {
      // Single-scope resource published under a different value — take it.
      // Never collapse multiple independently-required permissions (for
      // example Power Pages Read + Write) onto one unrelated scope.
      const only = enabled[0]!;
      resolved.push({ value: only.value, id: only.id });
      usedSingleScopeFallback = true;
      continue;
    }
    unresolved.push(value);
  }

  // De-duplicate ids while preserving order
  const seen = new Set<string>();
  const deduped = resolved.filter((r) => (seen.has(r.id) ? false : (seen.add(r.id), true)));

  return { resolved: deduped, unresolved, usedSingleScopeFallback };
}

/**
 * Build the tenant-correct requiredResourceAccess for the selected features:
 * ensure each resource SP exists, then resolve scope values → live ids.
 */
export async function buildResolvedResourceAccess(
  features: readonly FeatureId[],
  options: BuildResolvedResourceAccessOptions = {},
): Promise<ProvisioningReport> {
  const desired: DesiredResourceScopes[] = getDesiredResourceScopesForFeatures(features);
  const rra: RequiredResourceAccess[] = [];
  const resolutions: ScopeResolution[] = [];
  const warnings: string[] = [];
  const ensureSp = options.ensureServicePrincipal ?? ensureServicePrincipal;
  const readSp = options.getServicePrincipal ?? getServicePrincipal;
  const sleep =
    options.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  const retryDelays =
    options.scopeResolutionRetryDelaysMs ?? DEFAULT_SCOPE_RESOLUTION_RETRY_DELAYS_MS;

  for (const resource of desired) {
    const { sp: initialSp, created } = await ensureSp(resource.resourceAppId);
    let sp = initialSp;
    let resolution = resolveScopeIds(
      sp.oauth2PermissionScopes,
      [...resource.scopeValues],
      [...resource.alternateScopeValues],
    );

    // A newly-created first-party SP can be readable before its scope
    // collection has propagated. Power Pages has no safe static GUID fallback,
    // so poll its live metadata before deciding setup cannot continue.
    if (resource.resourceAppId === POWER_PLATFORM_API_APP_ID) {
      for (const delayMs of retryDelays) {
        const missingRequired = resolution.unresolved.filter((value) =>
          REQUIRED_POWER_PLATFORM_SCOPE_VALUE_SET.has(value),
        );
        if (missingRequired.length === 0) break;

        await sleep(delayMs);
        const refreshed = await readSp(resource.resourceAppId);
        if (!refreshed) continue;
        sp = refreshed;
        resolution = resolveScopeIds(
          sp.oauth2PermissionScopes,
          [...resource.scopeValues],
          [...resource.alternateScopeValues],
        );
      }

      const missingRequired = resolution.unresolved.filter((value) =>
        REQUIRED_POWER_PLATFORM_SCOPE_VALUE_SET.has(value),
      );
      if (missingRequired.length > 0) {
        throw new RequiredScopeResolutionError(
          resource.resourceAppId,
          missingRequired,
          sp.oauth2PermissionScopes.filter((scope) => scope.isEnabled).map((scope) => scope.value),
        );
      }
    }

    const { resolved, unresolved, usedSingleScopeFallback } = resolution;

    resolutions.push({
      resourceAppId: resource.resourceAppId,
      api: resource.api,
      resolved,
      publishedScopeIds: sp.oauth2PermissionScopes
        .filter((scope) => scope.isEnabled)
        .map((scope) => scope.id),
      unresolved,
      usedSingleScopeFallback,
      createdServicePrincipal: created,
    });

    if (unresolved.length > 0) {
      warnings.push(
        `${resource.api}: scope value(s) ${unresolved.map((v) => `"${v}"`).join(", ")} ` +
          `not published by the tenant's service principal — skipped. ` +
          `Published values: ${sp.oauth2PermissionScopes.map((s) => s.value).join(", ") || "(none)"}`,
      );
    }

    if (resolved.length > 0) {
      rra.push({
        resourceAppId: resource.resourceAppId,
        // Force a real array even for a single element — Graph rejects
        // scalar-collapsed collections with "1007 … does not match the schema".
        resourceAccess: resolved.map((r) => ({ id: r.id, type: "Scope" as const })),
      });
    }
  }

  return { requiredResourceAccess: rra, resolutions, warnings };
}

/**
 * Get the app registration's directory object id (needed for Graph PATCH).
 */
export async function getAppObjectId(clientId: string): Promise<string> {
  assertUuid(clientId, "client ID");
  const output = await runAzCommand([
    "ad",
    "app",
    "show",
    "--id",
    clientId,
    "--query",
    "id",
    "--output",
    "tsv",
  ]);
  const objectId = output.trim();
  assertUuid(objectId, "app object id");
  return objectId;
}

function parseDirectoryRequiredResourceAccess(value: unknown): DirectoryRequiredResourceAccess[] {
  if (value === null) return [];
  if (!Array.isArray(value)) {
    throw new Error(
      "Cannot safely update app permissions: requiredResourceAccess is not an array.",
    );
  }

  return value.map((resource, resourceIndex) => {
    if (!resource || typeof resource !== "object") {
      throw new Error(
        `Cannot safely update app permissions: resource entry ${resourceIndex} is invalid.`,
      );
    }
    const candidate = resource as {
      resourceAppId?: unknown;
      resourceAccess?: unknown;
    };
    if (typeof candidate.resourceAppId !== "string") {
      throw new Error(
        `Cannot safely update app permissions: resource entry ${resourceIndex} has no app ID.`,
      );
    }
    assertUuid(candidate.resourceAppId, `resource app ID at index ${resourceIndex}`);
    if (!Array.isArray(candidate.resourceAccess)) {
      throw new Error(
        `Cannot safely update app permissions: resource entry ${resourceIndex} has no access array.`,
      );
    }

    const resourceAccess = candidate.resourceAccess.map((access, accessIndex) => {
      if (!access || typeof access !== "object") {
        throw new Error(
          `Cannot safely update app permissions: access entry ${resourceIndex}:${accessIndex} is invalid.`,
        );
      }
      const item = access as { id?: unknown; type?: unknown };
      if (typeof item.id !== "string") {
        throw new Error(
          `Cannot safely update app permissions: access entry ${resourceIndex}:${accessIndex} has no ID.`,
        );
      }
      assertUuid(item.id, `permission ID at index ${resourceIndex}:${accessIndex}`);
      if (item.type !== "Scope" && item.type !== "Role") {
        throw new Error(
          `Cannot safely update app permissions: access entry ${resourceIndex}:${accessIndex} has invalid type.`,
        );
      }
      const type: DirectoryResourceAccess["type"] = item.type;
      return { id: item.id, type };
    });

    return { resourceAppId: candidate.resourceAppId, resourceAccess };
  });
}

/** Read the complete current collection so a repair never drops unrelated APIs. */
export async function getCurrentRequiredResourceAccess(
  clientId: string,
): Promise<DirectoryRequiredResourceAccess[]> {
  assertUuid(clientId, "client ID");
  const output = await runAzCommand([
    "ad",
    "app",
    "show",
    "--id",
    clientId,
    "--query",
    "requiredResourceAccess",
    "--output",
    "json",
  ]);

  let parsed: unknown;
  try {
    parsed = JSON.parse(output);
  } catch {
    throw new Error(
      "Cannot safely update app permissions: Azure CLI returned invalid requiredResourceAccess JSON.",
    );
  }
  return parseDirectoryRequiredResourceAccess(parsed);
}

function dedupeResourceAccess(
  entries: readonly DirectoryResourceAccess[],
): DirectoryResourceAccess[] {
  const seen = new Set<string>();
  return entries.filter((entry) => {
    const key = `${entry.type}:${entry.id.toLowerCase()}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Merge the resolved MCP scopes into the existing app manifest.
 *
 * Unselected resource APIs and their Scope/Role entries are preserved.
 * For a selected API, valid live delegated scopes and all application roles
 * are retained, stale/non-published delegated ids are removed, and the newly
 * resolved scopes are added. This both preserves unrelated permissions and
 * removes the bad canonical ids that this repair path exists to correct.
 */
export function mergeRequiredResourceAccess(
  existing: readonly DirectoryRequiredResourceAccess[],
  replacements: readonly RequiredResourceAccess[],
  resolutions: readonly ScopeResolution[],
): DirectoryRequiredResourceAccess[] {
  const existingByResource = new Map<string, DirectoryResourceAccess[]>();
  const replacementByResource = new Map(
    replacements.map((resource) => [resource.resourceAppId.toLowerCase(), resource]),
  );
  const resolutionByResource = new Map(
    resolutions.map((resolution) => [resolution.resourceAppId.toLowerCase(), resolution]),
  );
  const resourceAppIdByKey = new Map<string, string>();
  const resourceOrder: string[] = [];

  for (const resource of existing) {
    const key = resource.resourceAppId.toLowerCase();
    if (!existingByResource.has(key)) {
      resourceOrder.push(key);
      resourceAppIdByKey.set(key, resource.resourceAppId);
    }
    existingByResource.set(key, [
      ...(existingByResource.get(key) ?? []),
      ...resource.resourceAccess,
    ]);
  }
  for (const resource of replacements) {
    const key = resource.resourceAppId.toLowerCase();
    if (!resourceAppIdByKey.has(key)) {
      resourceOrder.push(key);
      resourceAppIdByKey.set(key, resource.resourceAppId);
    }
  }

  return resourceOrder.map((resourceKey) => {
    const resourceAppId = resourceAppIdByKey.get(resourceKey)!;
    const existingAccess = existingByResource.get(resourceKey) ?? [];
    const replacement = replacementByResource.get(resourceKey);
    if (!replacement) {
      return {
        resourceAppId,
        resourceAccess: dedupeResourceAccess(existingAccess),
      };
    }

    const resolution = resolutionByResource.get(resourceKey);
    if (!resolution) {
      throw new Error(
        `Cannot safely update app permissions: no live scope resolution for ${resourceAppId}.`,
      );
    }
    const publishedScopeIds = new Set(resolution.publishedScopeIds.map((id) => id.toLowerCase()));
    const preserved = existingAccess.filter(
      (access) => access.type === "Role" || publishedScopeIds.has(access.id.toLowerCase()),
    );

    return {
      resourceAppId,
      resourceAccess: dedupeResourceAccess([...preserved, ...replacement.resourceAccess]),
    };
  });
}

/** Check whether an app registration still exists (stale-config detection). */
export async function appRegistrationExists(clientId: string): Promise<boolean> {
  if (!UUID_RE.test(clientId)) return false;
  try {
    await getAppObjectId(clientId);
    return true;
  } catch {
    return false;
  }
}

/**
 * PATCH the app's requiredResourceAccess with the resolved set.
 * The body goes through a temp file (`--body @file`) because inline JSON
 * survives neither cmd.exe quoting nor `&` splitting on Windows. Node writes
 * UTF-8 without a BOM, which is what `az rest` requires.
 */
export async function patchRequiredResourceAccess(
  clientId: string,
  requiredResourceAccess: RequiredResourceAccess[],
  resolutions: readonly ScopeResolution[],
): Promise<DirectoryRequiredResourceAccess[]> {
  const existing = await getCurrentRequiredResourceAccess(clientId);
  const merged = mergeRequiredResourceAccess(existing, requiredResourceAccess, resolutions);
  const objectId = await getAppObjectId(clientId);

  const dir = mkdtempSync(join(tmpdir(), "pamcp-rra-"));
  const bodyPath = join(dir, "rra.json");
  try {
    writeFileSync(bodyPath, JSON.stringify({ requiredResourceAccess: merged }), {
      encoding: "utf-8",
    });
    await runAzCommand([
      "rest",
      "--method",
      "PATCH",
      "--url",
      `https://graph.microsoft.com/v1.0/applications/${objectId}`,
      "--headers",
      "Content-Type=application/json",
      "--body",
      `@${bodyPath}`,
    ]);
  } finally {
    rmSync(dir, { recursive: true, force: true });
  }
  return merged;
}

/**
 * Ensure the app allows public-client flows (device code) and has the
 * public-client redirect URIs registered. `az ad app create` paths that do
 * not set isFallbackPublicClient leave it false, and AAD then rejects
 * device-code with invalid_grant ("Device-code response is missing userCode").
 * Idempotent — safe to run on already-correct apps.
 */
export async function ensurePublicClientFlags(clientId: string): Promise<void> {
  assertUuid(clientId, "client ID");
  await runAzCommand([
    "ad",
    "app",
    "update",
    "--id",
    clientId,
    "--is-fallback-public-client",
    "true",
  ]);
}

/**
 * Ensure the CLIENT app itself has a service principal in the tenant.
 * Conditional Access evaluation and oauth2PermissionGrants both require it;
 * an app registration alone is not enough.
 */
export async function ensureClientServicePrincipal(clientId: string): Promise<boolean> {
  const { created } = await ensureServicePrincipal(clientId);
  return created;
}

/**
 * Grant tenant-wide admin consent for the app's configured permissions.
 * Requires the signed-in Azure CLI user to hold an admin role; callers
 * should offer this and fall back to the consent URL on failure.
 */
export async function grantAdminConsent(clientId: string): Promise<void> {
  assertUuid(clientId, "client ID");
  await runAzCommand(["ad", "app", "permission", "admin-consent", "--id", clientId]);
}

/**
 * Poll until the app registration reflects the PATCHed requiredResourceAccess.
 * App-registration changes take time to propagate to the sign-in service; a
 * too-fast token attempt reports the OLD permission ids (looks like the fix
 * failed when it didn't).
 */
export async function waitForResourceAccessPropagation(
  clientId: string,
  expected: readonly DirectoryRequiredResourceAccess[],
  opts: { timeoutMs?: number; intervalMs?: number } = {},
): Promise<boolean> {
  const timeoutMs = opts.timeoutMs ?? 60_000;
  const intervalMs = opts.intervalMs ?? 5_000;
  const expectedIds = new Set(
    expected.flatMap((r) =>
      r.resourceAccess.map((a) => `${r.resourceAppId.toLowerCase()}:${a.id.toLowerCase()}`),
    ),
  );

  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    try {
      const output = await runAzCommand([
        "ad",
        "app",
        "show",
        "--id",
        clientId,
        "--query",
        "requiredResourceAccess",
        "--output",
        "json",
      ]);
      const current = JSON.parse(output) as Array<{
        resourceAppId?: string;
        resourceAccess?: Array<{ id?: string }>;
      }> | null;
      const currentIds = new Set(
        (current ?? []).flatMap((r) =>
          (r.resourceAccess ?? []).map(
            (a) => `${r.resourceAppId?.toLowerCase()}:${a.id?.toLowerCase()}`,
          ),
        ),
      );
      if ([...expectedIds].every((id) => currentIds.has(id))) return true;
    } catch {
      // transient read failure — keep polling
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  return false;
}

export interface TenantProvisioningResult {
  report: ProvisioningReport;
  clientSpCreated: boolean;
  /** False when the RRA PATCH hadn't propagated within the poll window. */
  propagated: boolean;
}

/**
 * Full tenant hardening for an app the wizard resolved via Azure CLI.
 * Idempotent — every step is a no-op when already satisfied, so re-runs and
 * resumed-after-failure runs converge instead of spawning orphans:
 *   1. Ensure public-client flows are enabled on the app.
 *   2. Ensure the CLIENT service principal exists.
 *   3. Ensure each RESOURCE service principal exists (fixes AADSTS650052).
 *   4. Resolve scope values → tenant-local ids and PATCH requiredResourceAccess
 *      (fixes AADSTS65006).
 *   5. Wait for the PATCH to propagate to the sign-in service.
 */
export async function provisionTenantForApp(
  clientId: string,
  features: readonly FeatureId[],
): Promise<TenantProvisioningResult> {
  await ensurePublicClientFlags(clientId);
  const clientSpCreated = await ensureClientServicePrincipal(clientId);

  const report = await buildResolvedResourceAccess(features);
  let propagated = true;
  if (report.requiredResourceAccess.length > 0) {
    const appliedResourceAccess = await patchRequiredResourceAccess(
      clientId,
      report.requiredResourceAccess,
      report.resolutions,
    );
    propagated = await waitForResourceAccessPropagation(clientId, appliedResourceAccess);
  }

  return { report, clientSpCreated, propagated };
}

/**
 * Classify token errors that indicate directory-propagation lag or missing
 * provisioning rather than a genuine auth failure. Used by the wizard to
 * retry with backoff (65006), run the SP/RRA repair path (650052), or walk
 * the user through admin consent only when Microsoft actually demands it
 * (65001 — consent is verified by attempting sign-in, never asked about
 * up front).
 */
export function classifyProvisioningError(
  message: string,
): "missing-sp" | "bad-permission-ids" | "app-not-found" | "consent-required" | null {
  if (/AADSTS650052/i.test(message)) return "missing-sp";
  if (/AADSTS65006\b/i.test(message)) return "bad-permission-ids";
  if (/AADSTS700016/i.test(message)) return "app-not-found";
  if (/AADSTS65001/i.test(message)) return "consent-required";
  return null;
}
